import React from "react";

interface ShareNumbers {
  variant: 1 | 2 | 3;
  percentageOne: number;
  percentageTwo: number;
}

interface ShareLabels {
  labelOne?: string;
  labelTwo?: string;
}

interface HouseProgressBarProps {
  size: number;
  progress: number;
  id: string;
  baseColor: string;
  fillColor: string;
  shares: ShareNumbers;
  shareLabels?: ShareLabels;
}

const HouseProgressBar = (props: HouseProgressBarProps) => {
  const { size, id, progress, baseColor, fillColor, shares, shareLabels } = props;
  const progressSize = (progress / 100) * size;

  const getShareLabels = () => {
    if (shareLabels) {
      return (
        <div className="flex justify-center gap-3 mt-2">
          {shareLabels.labelOne && (
            <span style={{ color: fillColor }} className="text-xs font-bold">
              {shareLabels.labelOne}
            </span>
          )}
          {shareLabels.labelTwo && (
            <span style={{ color: baseColor }} className="text-xs font-bold">
              {shareLabels.labelTwo}
            </span>
          )}
        </div>
      );
    }
  };

  const getSharePercentages = () => {
    if (shares.variant === 1) {
      return (
        <>
          <div className="absolute flex justify-center items-center h-full w-full z-[1] text-base font-bold text-white -left-1/4">
            {shares.percentageOne}%
          </div>
          <div className="absolute flex justify-center items-center h-full w-full z-[1] text-base font-bold text-white left-1/4">
            {shares.percentageTwo}%
          </div>
        </>
      );
    }

    if (shares.variant === 2) {
      return (
        <>
          <div className="absolute flex justify-center items-center h-full w-full z-[1] text-base font-bold text-white -left-[5%]">
            {shares.percentageOne}%
          </div>
          <div
            style={{ color: baseColor }}
            className="absolute flex justify-center items-center h-full w-full z-[1] text-base font-bold text-white left-[65%]"
          >
            {shares.percentageTwo}%
          </div>
        </>
      );
    }

    if (shares.variant === 3) {
      return (
        <div className="flex justify-center items-center h-full w-full relative z-[1] text-base font-bold text-white">
          {shares.percentageOne}%
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative"
        style={{
          height: size,
          width: size,
        }}
      >
        <div
          className="absolute top-0 left-0 h-full w-full"
          style={{
            clipPath: `url("#${id}")`,
          }}
        ></div>

        {getSharePercentages()}

        <svg
          className="absolute top-0 left-0"
          width={size - 1}
          height={size - 1}
          viewBox={`0 0 ${size} ${size}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <clipPath id={`#${id}`}>
              <path
                fill={fillColor}
                d="m105.891 131.836c-7.879 0-15.759 0-23.664-.05-.185-.174-.328-.333-.507-.415-1.739-.798-2.44-2.122-2.431-4.051.049-10.046.066-20.093 0-30.139-.024-3.678-2.72-6.324-6.26-6.445-4.105-.141-8.221-.115-12.328-.007-3.418.09-6.272 2.716-6.277 6.37-.012 10.117-.004 20.233-.004 30.35 0 2.095-.731 3.172-2.667 3.979-.13.054-.192.269-.286.408-7.809 0-15.618 0-23.463-.074-.143-.159-.243-.308-.358-.321-2.927-.324-5.36-1.626-7.514-3.601-2.936-2.692-4.471-6.049-4.495-9.96-.077-12.72-.029-25.441-.029-38.162 0-.404 0-.808 0-1.251-.791 0-1.423.027-2.052-.008-.62-.034-1.237-.125-1.854-.201-4.778-.591-9.126-4.846-9.823-9.734-.021-.147-.265-.263-.404-.393 0-1.13 0-2.259.063-3.412.154-.126.313-.221.327-.333.367-2.97 1.953-5.252 4.009-7.306 17.481-17.465 34.954-34.938 52.418-52.42 2.175-2.178 4.797-3.414 7.821-3.573 3.452-.181 6.59.872 9.072 3.349 17.64 17.604 35.254 35.235 52.874 52.859 1.484 1.484 2.658 3.172 3.358 5.171.056 .16.275 .263.418 .392 0 2.254 0 4.509-.058 6.79-.181.262-.33.487-.423.733-1.513 3.987-4.245 6.67-8.438 7.656-1.531.36-3.134.416-4.791.621 0 .346 0 .761 0 1.176 0 12.372-.003 24.744.002 37.116.001 2.707-.54 5.272-1.942 7.616-2.256 3.772-5.497 6.129-9.872 6.863-.159.027-.283.265-.423.405z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </clipPath>
          </defs>
          <path
            fill={baseColor}
            d="m105.891 131.836c-7.879 0-15.759 0-23.664-.05-.185-.174-.328-.333-.507-.415-1.739-.798-2.44-2.122-2.431-4.051.049-10.046.066-20.093 0-30.139-.024-3.678-2.72-6.324-6.26-6.445-4.105-.141-8.221-.115-12.328-.007-3.418.09-6.272 2.716-6.277 6.37-.012 10.117-.004 20.233-.004 30.35 0 2.095-.731 3.172-2.667 3.979-.13.054-.192.269-.286.408-7.809 0-15.618 0-23.463-.074-.143-.159-.243-.308-.358-.321-2.927-.324-5.36-1.626-7.514-3.601-2.936-2.692-4.471-6.049-4.495-9.96-.077-12.72-.029-25.441-.029-38.162 0-.404 0-.808 0-1.251-.791 0-1.423.027-2.052-.008-.62-.034-1.237-.125-1.854-.201-4.778-.591-9.126-4.846-9.823-9.734-.021-.147-.265-.263-.404-.393 0-1.13 0-2.259.063-3.412.154-.126.313-.221.327-.333.367-2.97 1.953-5.252 4.009-7.306 17.481-17.465 34.954-34.938 52.418-52.42 2.175-2.178 4.797-3.414 7.821-3.573 3.452-.181 6.59.872 9.072 3.349 17.64 17.604 35.254 35.235 52.874 52.859 1.484 1.484 2.658 3.172 3.358 5.171.056 .16.275 .263.418 .392 0 2.254 0 4.509-.058 6.79-.181.262-.33.487-.423.733-1.513 3.987-4.245 6.67-8.438 7.656-1.531.36-3.134.416-4.791.621 0 .346 0 .761 0 1.176 0 12.372-.003 24.744.002 37.116.001 2.707-.54 5.272-1.942 7.616-2.256 3.772-5.497 6.129-9.872 6.863-.159.027-.283.265-.423.405z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <svg
          className="absolute top-0 left-0"
          width={progressSize}
          height={size}
          viewBox={`0 0 ${progressSize + 1} ${size + 1}`}
        >
          <path
            fill={fillColor}
            d="m105.891 131.836c-7.879 0-15.759 0-23.664-.05-.185-.174-.328-.333-.507-.415-1.739-.798-2.44-2.122-2.431-4.051.049-10.046.066-20.093 0-30.139-.024-3.678-2.72-6.324-6.26-6.445-4.105-.141-8.221-.115-12.328-.007-3.418.09-6.272 2.716-6.277 6.37-.012 10.117-.004 20.233-.004 30.35 0 2.095-.731 3.172-2.667 3.979-.13.054-.192.269-.286.408-7.809 0-15.618 0-23.463-.074-.143-.159-.243-.308-.358-.321-2.927-.324-5.36-1.626-7.514-3.601-2.936-2.692-4.471-6.049-4.495-9.96-.077-12.72-.029-25.441-.029-38.162 0-.404 0-.808 0-1.251-.791 0-1.423.027-2.052-.008-.62-.034-1.237-.125-1.854-.201-4.778-.591-9.126-4.846-9.823-9.734-.021-.147-.265-.263-.404-.393 0-1.13 0-2.259.063-3.412.154-.126.313-.221.327-.333.367-2.97 1.953-5.252 4.009-7.306 17.481-17.465 34.954-34.938 52.418-52.42 2.175-2.178 4.797-3.414 7.821-3.573 3.452-.181 6.59.872 9.072 3.349 17.64 17.604 35.254 35.235 52.874 52.859 1.484 1.484 2.658 3.172 3.358 5.171.056 .16.275 .263.418 .392 0 2.254 0 4.509-.058 6.79-.181.262-.33.487-.423.733-1.513 3.987-4.245 6.67-8.438 7.656-1.531.36-3.134.416-4.791.621 0 .346 0 .761 0 1.176 0 12.372-.003 24.744.002 37.116.001 2.707-.54 5.272-1.942 7.616-2.256 3.772-5.497 6.129-9.872 6.863-.159.027-.283.265-.423.405z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {getShareLabels()}
    </div>
  );
};

export default HouseProgressBar;
